'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuth } from '@/features/auth/hooks'
import { useApplyReissuedTokens } from '@/features/auth/hooks/use-apply-reissued-tokens'
import {
  confirm2FASetup,
  confirmPasskeyLogin,
  confirmPasskeySetup,
  deletePasskey,
  disable2FA,
  fetchUserPasskeyList,
  get2FABackupCodes,
  getUserPasskeys,
  passkeyLogin,
  setup2FA,
  setupPasskey,
  // updateUserProfile,
  verify2FA,
  verify2FALogin,
} from '@/service/user-profile.api'
import {
  Confirm2faSetupResponse,
  ConfirmPasskeyLoginResponse,
  ConfirmPasskeySetupResponse,
  DeletePasskeyResponse,
  Disable2faResponse,
  Get2faBackupCodesRequest,
  Get2faBackupCodesResponse,
  PasskeyLoginResponse,
  Setup2faResponse,
  SetupPasskeyResponse,
  Verify2faLoginResponse,
  Verify2faResponse,
} from '@/types/generated/common/auth'
import type { ErrorResponse } from '@/types/service'

// Query keys
export const userProfileKeys = {
  all: (userId?: string | null) => ['userProfile', userId] as const,
  passkeys: (userId?: string | null) => [...userProfileKeys.all(userId), 'passkeys'] as const,
  backupCodes: (userId?: string | null) => [...userProfileKeys.all(userId), 'backupCodes'] as const,
}

// User passkeys query
export const useUserPasskeys = () => {
  const { accessToken, user } = useAuth()
  return useQuery({
    queryKey: userProfileKeys.passkeys(user?.userId),
    queryFn: () => getUserPasskeys({}),
    enabled: !!accessToken,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// 2FA setup mutation
export const useSetup2FA = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (response: Setup2faResponse) => void
  onError?: (error: ErrorResponse) => void
} = {}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: setup2FA,
    onSuccess,
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

// Confirm 2FA setup mutation
export const useConfirm2FASetup = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (response: Confirm2faSetupResponse) => void
  onError?: (error: ErrorResponse) => void
} = {}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: confirm2FASetup,
    onSuccess,
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

// Verify 2FA mutation
export const useVerify2FA = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (response: Verify2faResponse) => void
  onError?: (error: ErrorResponse) => void
} = {}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: verify2FA,
    onSuccess,
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

// Verify 2FA login mutation
export const useVerify2FALogin = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (response: Verify2faLoginResponse) => void
  onError?: (error: ErrorResponse) => void
} = {}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: verify2FALogin,
    onSuccess,
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

// Disable 2FA mutation
export const useDisable2FA = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (response: Disable2faResponse) => void
  onError?: (error: ErrorResponse) => void
} = {}) => {
  const applyReissuedTokens = useApplyReissuedTokens()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: disable2FA,
    onSuccess: async (response) => {
      // Disable-2FA nukes every other session and reissues the caller's own
      // tokens (proto a828940) — adopt them before anything else fires a request.
      await applyReissuedTokens(response)
      onSuccess?.(response)
    },
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

// Get 2FA backup codes. Some backends return a 400-style error when 2FA is disabled;
// callers that use this as a status check can opt into treating that as "disabled".
export const useGet2FABackupCodes = (
  payload: Get2faBackupCodesRequest,
  options?: { treatMissingAsDisabled?: boolean }
) => {
  const { accessToken, user } = useAuth()
  return useQuery({
    queryKey: [
      ...userProfileKeys.backupCodes(user?.userId),
      { treatMissingAsDisabled: options?.treatMissingAsDisabled ?? false },
    ],
    queryFn: async (): Promise<Get2faBackupCodesResponse> => {
      try {
        return await get2FABackupCodes(payload)
      } catch (error) {
        if (options?.treatMissingAsDisabled && (error as ErrorResponse)?.status === 400) {
          return {
            success: false,
            backupCodes: [],
            errorMessage: (error as ErrorResponse).message,
          }
        }

        throw error
      }
    },
    enabled: !!accessToken,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Setup passkey mutation
export const useSetupPasskey = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (response: SetupPasskeyResponse) => void
  onError?: (error: ErrorResponse) => void
} = {}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: setupPasskey,
    onSuccess,
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

// Confirm passkey setup mutation
export const useConfirmPasskeySetup = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (response: ConfirmPasskeySetupResponse) => void
  onError?: (error: ErrorResponse) => void
} = {}) => {
  const queryClient = useQueryClient()
  const { user } = useAuth()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: confirmPasskeySetup,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: userProfileKeys.passkeys(user?.userId) })
      onSuccess?.(response)
    },
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

// Passkey login mutation
export const usePasskeyLogin = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (response: PasskeyLoginResponse) => void
  onError?: (error: ErrorResponse) => void
} = {}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: passkeyLogin,
    onSuccess,
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

// Confirm passkey login mutation
export const useConfirmPasskeyLogin = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (response: ConfirmPasskeyLoginResponse) => void
  onError?: (error: ErrorResponse) => void
} = {}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: confirmPasskeyLogin,
    onSuccess,
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

// Delete passkey mutation
export const useDeletePasskey = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (response: DeletePasskeyResponse) => void
  onError?: (error: ErrorResponse) => void
} = {}) => {
  const queryClient = useQueryClient()
  const applyReissuedTokens = useApplyReissuedTokens()

  const { user } = useAuth()

  const { mutateAsync, isPending } = useMutation({
    mutationFn: deletePasskey,
    onSuccess: async (response) => {
      // Passkey deletion also nuke-and-reissues (proto a828940).
      await applyReissuedTokens(response)
      queryClient.invalidateQueries({ queryKey: userProfileKeys.passkeys(user?.userId) })
      onSuccess?.(response)
    },
    onError,
  })

  return {
    mutateAsync,
    isPending,
  }
}

export const useGetUserPasskeyList = () => {
  const { user, accessToken } = useAuth()
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: userProfileKeys.passkeys(user?.userId),
    queryFn: () => fetchUserPasskeyList(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: Boolean(user?.emailVerified) && Boolean(accessToken),
  })

  return {
    data,
    isLoading,
    isError,
    refetch,
  }
}
